// T-36 — Menü an einer Markierung im Mailtext: „Als neuen Kontakt anlegen" /
// „Kopieren". Zwei Erscheinungsformen:
//   - `knopf`: nach dem Markieren ein kleiner Knopf „Kontakt anlegen" an der
//     Markierung — damit findet man den Weg auch ohne Rechtsklick (die Nutzer
//     arbeiten mit Windows/Edge und ohne Technikwissen).
//   - `menue`: per Rechtsklick auf die Markierung, mit „Kopieren", weil das
//     Browsermenü dafür abgefangen wurde.
// Portaliert nach `document.body`, fixed positioniert, über den Fenstern.
// Escape, ein Klick daneben oder Scrollen schließt es.

import * as React from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { PersonAdd20Regular, Copy20Regular } from "@fluentui/react-icons";
import { REFINED } from "../risikoanalyseStep/tokens";
import { Z_LAYERS } from "../windowManager/zLayers";
import { alleDokumente, alleFenster, aktivesFenster } from "../windowManager/browserFenster/fensterRegister";
import { istDom } from "../windowManager/browserFenster/domArt";

export type MarkierungsMenueProps = {
    text: string;
    /** Bezugspunkt in Fenster-Koordinaten (unterhalb der Markierung). */
    position: { x: number; y: number };
    modus: "knopf" | "menue";
    onAnlegen: (text: string) => void;
    onSchliessen: () => void;
};

export const MarkierungsMenue: React.FC<MarkierungsMenueProps> = ({
    text,
    position,
    modus,
    onAnlegen,
    onSchliessen,
}) => {
    const ref = React.useRef<HTMLDivElement | null>(null);

    React.useEffect(() => {
        const beiTaste = (e: KeyboardEvent) => {
            if (e.key === "Escape") onSchliessen();
        };
        const beiKlick = (e: MouseEvent) => {
            if (ref.current && istDom(e.target, "Node") && ref.current.contains(e.target)) return;
            onSchliessen();
        };
        const beiScroll = () => onSchliessen();
        alleDokumente.addEventListener("keydown", beiTaste);
        alleDokumente.addEventListener("mousedown", beiKlick);
        alleFenster.addEventListener("scroll", beiScroll, true);
        return () => {
            alleDokumente.removeEventListener("keydown", beiTaste);
            alleDokumente.removeEventListener("mousedown", beiKlick);
            alleFenster.removeEventListener("scroll", beiScroll, true);
        };
    }, [onSchliessen]);

    const kopieren = async () => {
        try {
            await navigator.clipboard?.writeText(text);
        } catch {
            /* Zwischenablage gesperrt — dann eben nicht */
        }
        onSchliessen();
    };

    // Im sichtbaren Bereich halten.
    const breite = modus === "knopf" ? 170 : 240;
    const x = Math.max(8, Math.min(position.x, (typeof window !== "undefined" ? aktivesFenster().innerWidth : 1200) - breite - 8));
    const y = Math.max(8, Math.min(position.y, (typeof window !== "undefined" ? aktivesFenster().innerHeight : 800) - 100));

    const inhalt =
        modus === "knopf" ? (
            <button
                type="button"
                style={S.knopf}
                onClick={() => onAnlegen(text)}
                title="Aus dem markierten Text einen neuen Kontakt anlegen"
            >
                <PersonAdd20Regular /> Kontakt anlegen
            </button>
        ) : (
            <div role="menu" aria-label="Markierung" style={S.menue}>
                <button type="button" role="menuitem" style={S.eintrag} onClick={() => onAnlegen(text)}>
                    <PersonAdd20Regular /> Als neuen Kontakt anlegen
                </button>
                <button type="button" role="menuitem" style={S.eintrag} onClick={() => void kopieren()}>
                    <Copy20Regular /> Kopieren
                </button>
            </div>
        );

    return createPortal(
        <div ref={ref} style={{ position: "fixed", left: x, top: y, zIndex: Z_LAYERS.ueberFenster + 5 }}>
            {inhalt}
        </div>,
        document.body,
    );
};

const S: Record<string, React.CSSProperties> = {
    knopf: {
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        border: `1px solid ${REFINED.aub}`,
        background: REFINED.aub,
        color: "#fff",
        borderRadius: 8,
        padding: "5px 10px",
        fontFamily: REFINED.fontUI,
        fontSize: 12.5,
        fontWeight: 600,
        cursor: "pointer",
        boxShadow: "0 6px 18px rgba(45,20,43,0.22)",
    },
    menue: {
        display: "flex",
        flexDirection: "column",
        minWidth: 220,
        background: "#fff",
        border: `1px solid ${REFINED.line}`,
        borderRadius: 10,
        padding: 4,
        boxShadow: "0 12px 32px rgba(45,20,43,0.18)",
        fontFamily: REFINED.fontUI,
    },
    eintrag: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        border: "none",
        background: "transparent",
        color: REFINED.ink,
        textAlign: "left",
        padding: "8px 10px",
        borderRadius: 6,
        fontSize: 13,
        cursor: "pointer",
    },
};

export default MarkierungsMenue;
